import React from "react";
import { Link } from "react-router-dom";
import { AlertCircle, RefreshCw, Home, Bug } from "lucide-react";

/**
 * Top-level error boundary — catches any unhandled React render error
 * across the app and shows a graceful fallback instead of a blank/black screen.
 * Also logs error to console + sends to backend for admin alerting.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    this.setState({ info });
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info);
    // Best-effort backend log (don't block render if it fails)
    try {
      const url = (process.env.REACT_APP_BACKEND_URL || "") + "/api/_client-error";
      fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: String(error?.message || error),
          stack: String(error?.stack || ""),
          component_stack: String(info?.componentStack || ""),
          path: typeof window !== "undefined" ? window.location.pathname : "",
          ua: typeof navigator !== "undefined" ? navigator.userAgent : "",
        }),
      }).catch(() => {});
    } catch (_) { /* ignore */ }
  }

  reset = () => this.setState({ hasError: false, error: null, info: null });

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center p-6" style={{ fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}>
        <div className="max-w-md w-full bg-[#121212] border border-white/10 rounded-2xl p-8 text-center">
          <div className="w-14 h-14 rounded-full bg-red-500/15 border border-red-500/30 flex items-center justify-center mx-auto">
            <AlertCircle className="w-7 h-7 text-red-400" />
          </div>
          <h1 className="text-2xl font-bold mt-4" style={{ fontFamily: "'Outfit', system-ui, sans-serif" }}>Something went wrong</h1>
          <p className="text-sm text-white/60 mt-2">An unexpected error occurred while loading this page. Our team has been notified.</p>
          {this.state.error && (
            <details className="mt-4 text-left bg-[#0a0a0a] border border-white/10 rounded-lg p-3" data-testid="error-boundary-details">
              <summary className="text-xs text-amber-300 cursor-pointer flex items-center gap-1.5"><Bug className="w-3 h-3" /> Click to view technical details</summary>
              <div className="mt-3 space-y-2">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-white/40 mb-0.5">Error message</div>
                  <pre className="text-[11px] text-rose-300 whitespace-pre-wrap break-all">{String(this.state.error?.message || this.state.error)}</pre>
                </div>
                {this.state.error?.stack && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-white/40 mb-0.5">Stack trace</div>
                    <pre className="text-[10px] text-white/60 overflow-auto max-h-40 whitespace-pre-wrap">{String(this.state.error.stack)}</pre>
                  </div>
                )}
                {this.state.info?.componentStack && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-white/40 mb-0.5">React component stack</div>
                    <pre className="text-[10px] text-white/60 overflow-auto max-h-32 whitespace-pre-wrap">{String(this.state.info.componentStack)}</pre>
                  </div>
                )}
                <div className="text-[10px] text-white/40 pt-2 border-t border-white/5">
                  📋 Copy the above and share with developer to debug. Errors are also auto-logged — admin can view all at <code className="text-fuchsia-300">/api/admin/client-errors</code>.
                </div>
              </div>
            </details>
          )}
          <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
            <button onClick={() => { this.reset(); window.location.reload(); }} className="inline-flex items-center gap-2 bg-[#FF0066] hover:bg-[#e6005c] text-white text-sm font-semibold rounded-full px-5 py-2.5 transition" data-testid="error-reload">
              <RefreshCw className="w-4 h-4" /> Reload page
            </button>
            <Link to="/" onClick={this.reset} className="inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/15 text-white text-sm font-medium rounded-full px-5 py-2.5 transition">
              <Home className="w-4 h-4" /> Go home
            </Link>
          </div>
          <div className="mt-5 text-[11px] text-white/40">
            Need help? <a href="https://wa.me/919131341638" target="_blank" rel="noreferrer" className="text-emerald-300">WhatsApp +91 91313 41638</a>
          </div>
        </div>
      </div>
    );
  }
}
